import { SortAndPage } from "@/models/ui/sortAndPage";
import { http } from "../api_v2";
import {
  ListVoyagesResponse,
  ListVoyagesResponseSchema,
  VoyageModel,
  VoyageModelSchema,
  AddVoyageCertificateOfQualityModel,
  ListVoyageCertificateOfQualityResponseSchema,
  ListVoyageShipTanksResponseSchema,
  AddEditVoyageShipTank,
  UpdateVoyageCertificateOfQualityModel,
  ComputedCertificateOfQualityModelSchema,
} from "./types";
import { z } from "zod";

/**
 * Returns a given voyage for a given Id
 * @param voyageId The ID for the voyage to return.
 * @returns The voyage.
 */
async function getVoyage(voyageId: number) {
  const response = await http.get(`/voyages/${voyageId}`);
  return VoyageModelSchema.parse(response.data);
}

/**
 * Lists all the voyages, with pagination and sorting.
 * @param pagination The pagination and sorting options to use.
 * @returns A list of voyage, paginated and sorted.
 */
async function listVoyages(
  pagination: SortAndPage,
): Promise<ListVoyagesResponse> {
  const planResponse = await http.get(`/voyages`, {
    params: {
      offset: pagination.offset,
      limit: pagination.limit,
      sortBy: pagination.sortBy,
      sortDirection: pagination.sortDirection,
      filter: pagination.search,
    },
  });
  return ListVoyagesResponseSchema.parse(planResponse.data);
}

/**
 * Creates a new voyage.
 * @param voyage The voyage to be created (Name / Vessel)
 * @returns The id of the newly created voyage.
 */
async function createVoyage(voyage: VoyageModel) {
  const response = await http.post("/voyages", {
    name: voyage.name,
    vessel: voyage.vessel,
  });

  // The voyage id is returned as a url within the headers. This just splits
  // it out to get the actual id.
  const url = URL.parse(response.headers.location);
  const planId = url?.pathname.split("/").at(-1);
  return z.coerce.number().parse(planId);
}

/**
 * Updates an existing voyage.
 * @param voyage The voyage to be created (Name / Vessel)
 */
async function updateVoyage(voyage: VoyageModel) {
  await http.put(`/voyages/${voyage.id}`, {
    id: voyage.id,
    name: voyage.name,
    vessel: voyage.vessel,
  });
}

/**
 * Lists all the ship tanks linked to a CoQ for the current voyage.
 *
 * @param pagination The pagination and sorting options to use.
 * @param voyageId The voyage Id of the current voyage/vessel.
 * @returns A list of ship tanks.
 */
async function ListVoyageShipTanks(
  voyageId: number,
  pagination: SortAndPage = {},
) {
  const response = await http.get(`/voyages/${voyageId}/ship-tanks`, {
    params: {
      offset: pagination.offset,
      limit: pagination.limit,
      sortBy: pagination.sortBy,
      sortDirection: pagination.sortDirection,
      filter: pagination.search,
    },
  });
  return ListVoyageShipTanksResponseSchema.parse(response.data);
}

/**
 * Lists all of the certificates of quality linked to a given voyage.
 *
 * @param voyageId The voygae to query on.
 * @param pagination Any pagination configuration etc.
 * @returns A list of certificates of quality.
 */
async function listCertificatesOfQuality(
  voyageId: number,
  pagination: SortAndPage,
) {
  const response = await http.get(
    `/voyages/${voyageId}/certificates-of-quality`,
    {
      params: {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
        filter: pagination.search,
      },
    },
  );

  return await ListVoyageCertificateOfQualityResponseSchema.parseAsync(
    response.data,
  );
}

/**
 * Creates a new Voyage CoQ from the model provided.
 *
 * @param shipCoqModel A coq model
 */
async function createCertificateOfQuality(
  voyageId: number,
  voyageCoqModel: AddVoyageCertificateOfQualityModel,
) {
  await http.post(
    `/voyages/${voyageId}/certificates-of-quality`,
    voyageCoqModel,
  );
}

async function updateVoyageCertificateOfQuality(
  voyageId: number,
  voyageCertificateOfQualityId: number,
  voyageCoqModel: UpdateVoyageCertificateOfQualityModel,
) {
  await http.put(
    `/voyages/${voyageId}/certificates-of-quality/${voyageCertificateOfQualityId}`,
    voyageCoqModel,
  );
}

/**
 * Deletes a voyage certificate of quality, can only be used
 * if the certificate has no linked ship-tanks.
 *
 * @param voyageId The voyage to remove this ship-tank from.
 * @param voyageCertificateOfQualityId The id of the certificate to remove.
 */
async function deleteVoyageCertificateOfQuality(
  voyageId: number,
  voyageCertificateOfQualityId: number,
) {
  await http.delete(
    `/voyages/${voyageId}/certificates-of-quality/${voyageCertificateOfQualityId}`,
  );
}

/**
 * Creates a new voyage ship tank. This is a mapping between
 * certificates and ship tanks that determines complex allocations.
 *
 * @param voyageId The ID of the voyage to link this to.
 * @param addVoyageShipTank The request detaling the links to create.
 */
async function addVoyageShipTank(
  voyageId: number,
  addVoyageShipTank: AddEditVoyageShipTank,
) {
  await http.post(`/voyages/${voyageId}/ship-tanks`, addVoyageShipTank);
}

/**
 * Updates an existing voyage ship tank.
 *
 * If none exists already it will throw an error.
 *
 * Note: The ship tank id can't change (You can't point to a new ship tank id).
 *
 * @param voyageId The voygae ID of the ship tank.
 * @param shipTankId The Ship Tank ID to link to.
 * @param addVoyageShipTank The new state of the ship tank to apply.
 */
async function updateVoyageShipTank(
  voyageId: number,
  shipTankId: number,
  addVoyageShipTank: AddEditVoyageShipTank,
) {
  await http.put(
    `/voyages/${voyageId}/ship-tanks/${shipTankId}`,
    addVoyageShipTank,
  );
}

/**
 * Deletes a voyage ship tank, will remove all links to this ship tank from any COQ on this
 * voyage.
 *
 * If none are found, will throw any error.
 * @param voyageId The voyage to remove this ship-tank from.
 * @param shipTankId The id of the ship tank to remove.
 */
async function deleteVoyageShipTank(voyageId: number, shipTankId: number) {
  await http.delete(`/voyages/${voyageId}/ship-tanks/${shipTankId}`);
}

async function getComputedCertifiacteOfQuality(
  voyageId: number,
  shipTankIds: number[],
) {
  const response = await http.get(
    `/voyages/${voyageId}/certificates-of-quality/computed`,
    {
      params: {
        shipTankIds: shipTankIds,
      },
      paramsSerializer: {
        indexes: null,
      },
    },
  );

  return await ComputedCertificateOfQualityModelSchema.parseAsync(
    response.data,
  );
}

export default {
  getVoyage,
  createVoyage,
  listVoyages,
  updateVoyage,
  ListVoyageShipTanks,
  createCertificateOfQuality,
  updateVoyageCertificateOfQuality,
  deleteVoyageCertificateOfQuality,
  listCertificatesOfQuality,
  addVoyageShipTank,
  updateVoyageShipTank,
  deleteVoyageShipTank,
  getComputedCertifiacteOfQuality,
};
